import { createSlice, nanoid, type PayloadAction } from "@reduxjs/toolkit";

import type { ExceptionDetail, ExceptionPatch } from "@/types/api";

interface ExceptionsState {
  items: ExceptionDetail[];
}

const TODAY = "2026-05-25";

const seed: ExceptionDetail[] = [
  {
    id: "exc-1",
    kind: "no_mapping",
    severity: "critical",
    status: "open",
    raw_event_id: "rce-1",
    detail: { cloudbeds_code: "RR-VIP", reservation: "R-10250" },
    resolution_note: null,
    resolved_at: null,
    resolved_by: null,
    created_at: `${TODAY}T03:00:30.000Z`,
    raw_event: {
      id: "rce-1",
      event_type: "reservation/created",
      payload: {
        reservation_id: "R-10250",
        guest: "Doe, Jane",
        arrival: "2026-05-22",
        departure: "2026-05-24",
        gl_code: "RR-VIP",
        total_amount: { amount: 32000, currency: "EUR" },
      },
      received_at: `${TODAY}T03:00:30.000Z`,
    },
  },
  {
    id: "exc-2",
    kind: "unmatched_ota",
    severity: "high",
    status: "open",
    raw_event_id: "rce-2",
    detail: { channel: "booking.com", expected_minor: 184500, week: "2026-W19" },
    resolution_note: null,
    resolved_at: null,
    resolved_by: null,
    created_at: "2026-05-22T04:00:00.000Z",
    raw_event: {
      id: "rce-2",
      event_type: "booking.weekly_settlement",
      payload: { channel: "booking.com", week: "2026-W19", expected_minor: 184500 },
      received_at: "2026-05-22T04:00:00.000Z",
    },
  },
  {
    id: "exc-3",
    kind: "variance",
    severity: "standard",
    status: "investigating",
    raw_event_id: null,
    detail: { variance_minor: 4250, report: "guest-ledger", date: "2026-05-14" },
    resolution_note: null,
    resolved_at: null,
    resolved_by: null,
    created_at: "2026-05-15T08:12:00.000Z",
    raw_event: null,
  },
  {
    id: "exc-4",
    kind: "sfm_reject",
    severity: "high",
    status: "resolved",
    raw_event_id: "rce-9",
    detail: { reason: "duplicate transaction id", txn: "TX-998" },
    resolution_note: "Re-queued with new idempotency key.",
    resolved_at: "2026-05-10T11:32:00.000Z",
    resolved_by: "dev-user",
    created_at: "2026-05-10T10:00:00.000Z",
    raw_event: null,
  },
];

const initialState: ExceptionsState = { items: seed };

const slice = createSlice({
  name: "exceptions",
  initialState,
  reducers: {
    patchException(
      state,
      action: PayloadAction<{ id: string; patch: ExceptionPatch; actor?: string }>,
    ) {
      const exc = state.items.find((e) => e.id === action.payload.id);
      if (!exc) return;
      exc.status = action.payload.patch.status;
      exc.resolution_note = action.payload.patch.resolution_note ?? null;
      if (action.payload.patch.status === "resolved") {
        exc.resolved_at = new Date().toISOString();
        exc.resolved_by = action.payload.actor ?? "dev-user";
      } else {
        exc.resolved_at = null;
        exc.resolved_by = null;
      }
    },
    addException(
      state,
      action: PayloadAction<Omit<ExceptionDetail, "id" | "created_at">>,
    ) {
      const id = `exc-${nanoid(6)}`;
      state.items.unshift({
        ...action.payload,
        id,
        created_at: new Date().toISOString(),
      });
    },
    deleteException(state, action: PayloadAction<string>) {
      state.items = state.items.filter((e) => e.id !== action.payload);
    },
  },
});

export const { patchException, addException, deleteException } = slice.actions;
export default slice.reducer;
